import { useState, useRef } from "react";

// =====================================================
// CONSTANTS
// =====================================================
const CALENDLY_URL = "https://calendly.com/dhihospitality-audit-review/30min?hide_event_type_details=1&hide_gdpr_banner=1";

const TIER_COLORS = {
  1: "#2D4DE0",
  2: "#1A3AB8", 
  3: "#0F2A8A"
};

const PARAMETER_NAMES = {
  brandSearchVisibility: "Brand Visibility & Dominance",
  rateParityChannelCompetitiveness: "Rate Parity & Channel Competitiveness",
  gmbOptimisation: "Google Business Profile Effectiveness",
  pageLoadPerformance: "Page Load Performance",
  mobileBookingExperience: "Mobile Booking Experience",
  bookingSimplicity: "Booking Simplicity & Friction",
  directBookingIncentive: "Direct Booking Incentives & Infrastructure",
  contentDepthQuality: "Content Depth & Quality",
  socialProofReviewSignals: "Social Proof & Review Signals",
  paymentTrustSecurity: "Payment Trust & Security",
};

const TIER_PARAMETERS = {
  1: ["brandSearchVisibility", "rateParityChannelCompetitiveness", "gmbOptimisation"],
  2: ["pageLoadPerformance", "mobileBookingExperience", "bookingSimplicity"],
  3: ["directBookingIncentive", "contentDepthQuality", "socialProofReviewSignals", "paymentTrustSecurity"]
};

const TIER_INFO = {
  1: { name: "Market Presence & Discoverability", question: "Can guests find your hotel before OTAs capture the search?" },
  2: { name: "Booking Experience & Friction", question: "When guests arrive at your site, can they book without friction?" },
  3: { name: "Conversion Confidence & Credibility", question: "Are you giving guests a compelling reason to book direct?" }
};

// Parameter weights for tier calculation
const PARAMETER_WEIGHTS = {
  brandSearchVisibility: 14,
  rateParityChannelCompetitiveness: 14,
  gmbOptimisation: 8,
  pageLoadPerformance: 10,
  mobileBookingExperience: 12,
  bookingSimplicity: 16,
  directBookingIncentive: 11,
  contentDepthQuality: 6,
  socialProofReviewSignals: 5,
  paymentTrustSecurity: 4,
};

// =====================================================
// STYLE OBJECTS
// =====================================================
const badgeColors = {
  "badge-strong":   { background: "#EBF0FF", color: "#2D4DE0" },
  "badge-adequate": { background: "#EBF0FF", color: "#2D4DE0" },
  "badge-weak":     { background: "#F0F1F7", color: "#0A0F2E" },
  "badge-critical": { background: "#F0F1F7", color: "#0A0F2E" }
};

// Uniform badge styling
const badgeStyle = {
  minWidth: 48,
  textAlign: "center",
  padding: "2px 8px"
};

const statusColors = {
  reference: { background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE" },
  undercut: { background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA" },
  premium: { background: "#FFF7ED", color: "#EA580C", border: "1px solid #FED7AA" },
  parity: { background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0" },
  na: { background: "rgba(10,10,26,0.04)", color: "rgba(10,10,26,0.35)", border: "1px solid rgba(10,10,26,0.1)" }
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================
const getGrade = (score, max = 10) => {
  const normalized = max === 100 ? score : score * 10;
  if (normalized >= 90) return { label: "Strong", class: "badge-strong" };
  if (normalized >= 70) return { label: "Good", class: "badge-adequate" };
  if (normalized >= 40) return { label: "Weak", class: "badge-weak" };
  return { label: "Critical", class: "badge-critical" };
};

const formatCurrency = (amount, currency = "USD") => {
  if (!amount || isNaN(amount)) return `${currency} 0`;
  const symbols = { USD: "$", EUR: "€", GBP: "£", JPY: "¥", IDR: "Rp ", KRW: "₩", VND: "₫", CNY: "¥", HKD: "HK$", TWD: "NT$", PHP: "₱", THB: "THB ", INR: "₹", SGD: "S$", MYR: "RM ", BND: "B$", KHR: "KHR ", MMK: "K ", LKR: "Rs ", MVR: "Rf ", MUR: "Rs ", SCR: "Rs ", AED: "AED ", QAR: "QAR ", SAR: "SAR ", KWD: "KD ", BHD: "BD ", OMR: "OMR ", JOD: "JD ", EGP: "E£", TRY: "₺", ZAR: "R ", AUD: "A$", NZD: "NZ$", CAD: "C$", CHF: "CHF ", FJD: "FJ$", BRL: "R$" };
  const symbol = symbols[currency] || `${currency} `;
  
  if (amount >= 1000000) return `${symbol}${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${symbol}${(amount / 1000).toFixed(0)}K`;
  return `${symbol}${amount.toFixed(0)}`;
};

const formatNumber = (num) => {
  if (!num || isNaN(num)) return "0";
  return num.toLocaleString();
};

// Calculate weighted tier score from parameter scores
const calculateTierScore = (tier, scores) => {
  const params = TIER_PARAMETERS[tier];
  let totalWeightedScore = 0;
  let totalWeight = 0;
  
  params.forEach(param => {
    const paramScore = scores[param] || 0;
    const weight = PARAMETER_WEIGHTS[param] || 10;
    totalWeightedScore += paramScore * weight;
    totalWeight += weight;
  });
  
  // Return score out of 100
  return totalWeight > 0 ? Math.round((totalWeightedScore / totalWeight) * 10) : 0;
};

// =====================================================
// SVG COMPONENTS
// =====================================================
const ScoreRing = ({ score, size = 130, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2 - 8;
  const circumference = 2 * Math.PI * radius;
  const safeScore = Number(score) || 0;
  const offset = circumference - (safeScore / 100) * circumference;
  const displayScore = Math.round(safeScore);

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ position: "absolute", top: 0, left: 0 }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#F5F6FA" strokeWidth={strokeWidth}/>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#2D4DE0" strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}/>
      </svg>
      <div style={{ position: "absolute", top: 0, left: 0, width: size, height: size, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 36, fontWeight: 800, color: "#0A0F2E", lineHeight: 1 }}>{displayScore}</span>
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "rgba(10,10,26,0.25)", marginTop: 4 }}>out of 100</span>
      </div>
    </div>
  );
};

const TierGauge = ({ score, size = 110 }) => {
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const safeScore = Number(score) || 0;
  const offset = circumference - (safeScore / 100) * circumference;
  const displayScore = Math.round(safeScore);

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 110 110" style={{ position: "absolute", top: 0, left: 0 }}>
        <circle cx="55" cy="55" r={radius} fill="none" stroke="#F5F6FA" strokeWidth="7"/>
        <circle cx="55" cy="55" r={radius} fill="none" stroke="#2D4DE0" strokeWidth="7"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 55 55)"/>
      </svg>
      <div style={{ position: "absolute", top: 0, left: 0, width: size, height: size, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 26, fontWeight: 800, color: "#0A0F2E", lineHeight: 1 }}>{displayScore}</span>
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 500, color: "rgba(10,10,26,0.25)", marginTop: 2 }}>out of 100</span>
      </div>
    </div>
  );
};

// =====================================================
// BADGE COMPONENTS
// =====================================================
const Badge = ({ grade, small = false }) => {
  const colors = badgeColors[grade.class] || badgeColors["badge-weak"];
  return (
    <span style={{ fontSize: small ? 9 : 9.5, fontWeight: 600, borderRadius: 4, whiteSpace: "nowrap", display: "inline-block", ...badgeStyle, ...colors }}>
      {grade.label}
    </span>
  );
};

const StatusBadge = ({ status, small = true }) => {
  const colors = statusColors[status] || statusColors.na;
  const labels = { reference: "Reference", undercut: "Undercut", premium: "Premium", parity: "Parity", na: "N/A" };
  return (
    <span style={{ fontSize: 8, fontWeight: 700, borderRadius: 4, padding: "3px 10px", whiteSpace: "nowrap", display: "inline-block", ...colors }}>
      {labels[status] || status}
    </span>
  );
};

// =====================================================
// CTA TEASER COMPONENT
// =====================================================
const CTATeaser = () => (
  <div className="cta-teaser" style={{ background: "#0A0A1A", borderRadius: 14, padding: 24, position: "relative", overflow: "hidden" }}>
    <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>Next Step</div>
    <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 700, color: "#FFFFFF", marginBottom: 6 }}>Ready to fix these issues?</div>
    <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.5)", marginBottom: 16, lineHeight: 1.5 }}>A dhi strategist will walk you through the findings and tell you exactly what to fix first.</div>
    <button
      onClick={() => window.open('https://calendly.com/dhihospitality-audit-review/30min?hide_event_type_details=1&hide_gdpr_banner=1', '_blank')}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#2D4DE0", border: "none", borderRadius: 8, padding: "10px 20px", cursor: "pointer", transition: "all 0.2s" }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: "#FFFFFF" }}>Book a Strategy Call</span>
      <span style={{ color: "#FFFFFF" }}>→</span>
    </button>
  </div>
);

export {
  CALENDLY_URL, TIER_COLORS, PARAMETER_NAMES, TIER_PARAMETERS, TIER_INFO,
  PARAMETER_WEIGHTS, badgeColors, badgeStyle, statusColors,
  getGrade, formatCurrency, formatNumber, calculateTierScore,
  ScoreRing, TierGauge, Badge, StatusBadge, CTATeaser
};
